import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Squares2X2Icon, ShoppingBagIcon, UsersIcon, BuildingStorefrontIcon, InboxIcon, CubeIcon } from "@heroicons/react/24/outline";

const TYPE_ICON = {
  Product: CubeIcon, Seller: BuildingStorefrontIcon, Customer: UsersIcon, Order: ShoppingBagIcon,
};

export const CommandPalette = ({ open, setOpen, navItems = [], onNavigate }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const timer = useRef(null);

  const runSearch = useCallback((q, signal) => {
    if (q.trim().length < 2) { setResults([]); return; }
    api.get(`/admin/search?q=${encodeURIComponent(q)}`, { signal }).then((r) => setResults(r.data.results || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => runSearch(query, controller.signal), 250);
    return () => { if (timer.current) clearTimeout(timer.current); controller.abort(); };
  }, [query, runSearch]);

  useEffect(() => { if (!open) { setQuery(""); setResults([]); } }, [open]);

  const pick = (section) => { setOpen(false); onNavigate(section); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput data-testid="command-input" placeholder="Search products, sellers, orders, customers… or jump to a section" value={query} onValueChange={setQuery} />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {results.length > 0 && (
          <CommandGroup heading="Search results">
            {results.map((r, i) => {
              const Icon = TYPE_ICON[r.type] || Squares2X2Icon;
              return (
                <CommandItem key={`${r.type}-${r.id}-${i}`} value={`${r.label} ${r.type} ${r.id}`} onSelect={() => pick(r.section)} data-testid={`command-result-${i}`}>
                  <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="truncate">{r.label}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{r.type}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
        <CommandGroup heading="Go to">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem key={item.key} value={`goto ${item.label} ${item.group}`} onSelect={() => pick(item.key)} data-testid={`command-nav-${item.key}`}>
                <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                <span>{item.label}</span>
                <span className="ml-auto text-xs text-muted-foreground">{item.group}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
